/**
 * UI overhaul PR-1a — the tab-list reconciliation rule, extracted from
 * TabsContext so it is a pure function with tests.
 *
 * Given the persisted tab list and the live browser location, decide what the
 * list becomes:
 *   - the location's key matches an open tab → refresh that tab's snapshot,
 *     title and lastActiveAt (covers "activated an existing tab" and
 *     "navigated within the active tab", e.g. a sort or `?tab=` change — the
 *     title follows, so "Advertising · <scope>" relabels as you drill, PR 4);
 *   - no match and room → append a new tab;
 *   - no match and at capacity → close the least-recently-active UNPINNED
 *     tab and APPEND the new one at the end, with a notice naming what closed.
 *     (Before PR-1a the new tab was spliced into the evicted slot, so tabs
 *     reshuffled silently — audit APP-05.)
 *   - every tab pinned → replace the slot of the tab we navigated FROM (or the
 *     globally oldest), with a notice — nothing must ever refuse to render the
 *     location the browser is on.
 */
import type { TabLocationSnapshot, TabRecord } from './types'

export interface ReconcileInput {
  cur: readonly TabRecord[]
  key: string
  snap: TabLocationSnapshot
  title: string
  now: number
  maxTabs: number
  /** The key that was active going INTO this navigation (all-pinned fallback). */
  prevActiveKey: string | null
  /** The key of the location a REPLACE navigation replaced (history no longer
   *  holds it), when it differs from ``key``; null for a push, a pop or a
   *  replace within the same tab. */
  replacedKey?: string | null
}

export interface ReconcileResult {
  /** The next list, or the SAME array reference when nothing changed. */
  tabs: readonly TabRecord[]
  /** One-line explanation for the bar when a tab was closed or replaced. */
  notice: string | null
}

const SAME_LOCATION_DEBOUNCE_MS = 500

export function reconcileTabs(i: ReconcileInput): ReconcileResult {
  const { cur, key, snap, now } = i
  // A replace hands the tab over: the location it replaced is gone from
  // history, so its tab becomes this location's (same slot, same pin) instead
  // of staying open beside it. A saved "new" editor used to live on as a second
  // tab holding the already-saved form, its Save still enabled, next to the
  // record's own tab (review 2026-09-25, saved-filter-editor.spec).
  if (i.replacedKey && i.replacedKey !== key) {
    const from = cur.findIndex((t) => t.id === i.replacedKey)
    if (from !== -1) {
      const open = cur.findIndex((t) => t.id === key)
      if (open !== -1) {
        const next = cur.filter((_, n) => n !== from)
        const at = next.findIndex((t) => t.id === key)
        next[at] = { ...next[at], title: i.title, location: snap, lastActiveAt: now }
        return { tabs: next, notice: null }
      }
      const next = [...cur]
      next[from] = { id: key, title: i.title, location: snap, pinned: cur[from].pinned, lastActiveAt: now }
      return { tabs: next, notice: null }
    }
  }
  const idx = cur.findIndex((t) => t.id === key)
  if (idx !== -1) {
    const existing = cur[idx]
    if (
      existing.location.pathname === snap.pathname &&
      existing.location.search === snap.search &&
      existing.location.hash === snap.hash &&
      // lastActiveAt still needs a bump when the location is unchanged (e.g.
      // re-clicking the same pill) — but skip the write entirely when nothing
      // would change, to avoid a churny render loop on every location-object
      // identity change with the same value.
      now - existing.lastActiveAt < SAME_LOCATION_DEBOUNCE_MS
    ) {
      return { tabs: cur, notice: null }
    }
    const next = [...cur]
    // The title follows the location (PR 4, A6): a page whose title reads the
    // URL — the Advertising hub's scope, a hub's sub-tab — relabels its one
    // tab instead of keeping the title of the first visit.
    next[idx] = { ...existing, title: i.title, location: snap, lastActiveAt: now }
    return { tabs: next, notice: null }
  }

  const fresh: TabRecord = { id: key, title: i.title, location: snap, pinned: false, lastActiveAt: now }

  if (cur.length < i.maxTabs) {
    return { tabs: [...cur, fresh], notice: null }
  }

  // At capacity — close the least-recently-active UNPINNED tab, append the new one.
  let evictIdx = -1
  let evictAt = Infinity
  cur.forEach((t, n) => {
    if (!t.pinned && t.lastActiveAt < evictAt) { evictAt = t.lastActiveAt; evictIdx = n }
  })
  if (evictIdx !== -1) {
    const closed = cur[evictIdx]
    const next = cur.filter((_, n) => n !== evictIdx)
    next.push(fresh)
    return { tabs: next, notice: `Closed “${closed.title || closed.location.pathname}” to make room for this tab.` }
  }

  // Every open tab is pinned — replace the tab we navigated FROM rather than
  // growing past the cap or leaving the new location unrendered.
  const replaceIdx = i.prevActiveKey ? cur.findIndex((t) => t.id === i.prevActiveKey) : -1
  const next = [...cur]
  if (replaceIdx !== -1) {
    next.splice(replaceIdx, 1, fresh)
  } else {
    let oldestIdx = 0
    cur.forEach((t, n) => { if (t.lastActiveAt < cur[oldestIdx].lastActiveAt) oldestIdx = n })
    next.splice(oldestIdx, 1, fresh)
  }
  return {
    tabs: next,
    notice: `All ${i.maxTabs} tabs are pinned — unpin one to keep more open. Opened this in place of your last tab.`,
  }
}
